import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';

/**
 * Column Filter Component
 * Excel-like dropdown filter for a single column
 */
@Component({
  selector: 'app-column-filter',
  standalone: true,
  imports: [
    CommonModule,
    MatMenuModule,
    MatIconModule,
    MatCheckboxModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
  ],
  template: `
    <button
      mat-icon-button
      [matMenuTriggerFor]="filterMenu"
      #menuTrigger="matMenuTrigger"
      class="column-filter-trigger"
      [class.filter-active]="hasActiveFilter"
      (click)="$event.stopPropagation()">
      <mat-icon [class.text-primary]="hasActiveFilter">
        {{ hasActiveFilter ? 'filter_alt' : 'filter_list' }}
      </mat-icon>
    </button>

    <mat-menu #filterMenu="matMenu" class="column-filter-menu">
      <div class="p-4 min-w-[250px] max-h-[400px] flex flex-col" (click)="$event.stopPropagation()">
        <!-- Search Input -->
        <mat-form-field appearance="outline" class="w-full mb-2" *ngIf="searchable && filterOptions.length > 5">
          <mat-label>Buscar...</mat-label>
          <input
            matInput
            [(ngModel)]="searchTerm"
            (ngModelChange)="onSearchChange()"
            autocomplete="off">
          <mat-icon matSuffix>search</mat-icon>
        </mat-form-field>

        <!-- Select All Checkbox -->
        <mat-checkbox
          [checked]="allSelected"
          [indeterminate]="someSelected && !allSelected"
          (change)="toggleSelectAll($event.checked)"
          class="mb-2">
          <span class="text-sm font-semibold">Seleccionar Todo</span>
        </mat-checkbox>

        <div class="border-t border-gray-200 dark:border-gray-700 my-2"></div>

        <!-- Filter Options List -->
        <div class="flex-1 overflow-y-auto max-h-[250px]">
          <div *ngIf="filteredOptions.length === 0" class="text-center py-4 text-gray-500 text-sm">
            No se encontraron opciones
          </div>

          <mat-checkbox
            *ngFor="let option of filteredOptions; trackBy: trackByValue"
            [checked]="isOptionSelected(option.value)"
            (change)="toggleOption(option.value, $event.checked)"
            class="block mb-1">
            <span class="text-sm flex items-center justify-between w-full">
              
              <span class="text-xs text-gray-500">({{ option.count }})</span>
              <span class="truncate ml-2" [title]="option.label">{{ option.label }}</span>
            </span>
          </mat-checkbox>
        </div>

        <div class="border-t border-gray-200 dark:border-gray-700 my-2"></div>

        <!-- Action Buttons -->
        <div class="flex gap-2 justify-end">
          <button mat-stroked-button (click)="onClear()" class="text-sm">
            Limpiar
          </button>
          <button mat-flat-button color="primary" (click)="onApply()" class="text-sm">
            Aplicar
          </button>
        </div>
      </div>
    </mat-menu>
  `,
  styles: [`
    .column-filter-trigger {
      width: 32px;
      height: 32px;
      line-height: 32px;

      mat-icon {
        font-size: 18px;
        width: 18px;
        height: 18px;
      }
    }

    .filter-active {
      mat-icon {
        color: var(--mdc-theme-primary, #3f51b5);
      }
    }

    ::ng-deep .column-filter-menu {
      .mat-mdc-menu-content {
        padding: 0 !important;
      }
    }

    mat-checkbox {
      ::ng-deep .mdc-checkbox {
        padding: 8px;
      }

      ::ng-deep .mdc-form-field {
        width: 100%;
      }
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColumnFilterComponent implements OnInit, OnChanges {
  /**
   * Available filter options with counts
   */
  @Input() filterOptions: Array<{ value: any; label: string; count: number }> = [];

  /**
   * Currently selected values
   */
  @Input() selectedValues: any[] = [];

  /**
   * Enable search within filter options
   */
  @Input() searchable: boolean = true;

  /**
   * Emitted when filter is applied
   */
  @Output() filterApplied = new EventEmitter<any[]>();

  /**
   * Emitted when filter is cleared
   */
  @Output() filterCleared = new EventEmitter<void>();

  /**
   * ViewChild to access MatMenuTrigger for programmatic menu control
   */
  @ViewChild(MatMenuTrigger) menuTrigger!: MatMenuTrigger;

  // Internal state
  searchTerm: string = '';
  filteredOptions: Array<{ value: any; label: string; count: number }> = [];

  /**
   * Map to store selected values with serialized keys for object comparison
   * Key: Serialized value (JSON.stringify for objects, String for primitives)
   * Value: Original value
   */
  private valueMap: Map<string, any> = new Map();

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.filteredOptions = [...this.filterOptions];
    this.initializeValueMap();
  }

  /**
   * Initialize value map from selected values
   */
  private initializeValueMap(): void {
    this.valueMap.clear();
    this.selectedValues.forEach(value => {
      const key = this.serializeValue(value);
      this.valueMap.set(key, value);
    });
  }

  /**
   * Serialize value to string for use as Map key
   * Objects are serialized using JSON.stringify for value comparison
   */
  private serializeValue(value: any): string {
    if (value === null || value === undefined) {
      return String(value);
    }
    return typeof value === 'object' ? JSON.stringify(value) : String(value);
  }

  /**
   * Respond to input changes (critical for OnPush change detection)
   * Updates internal state when @Input() filterOptions or selectedValues change
   */
  ngOnChanges(changes: SimpleChanges): void {


    // Update filteredOptions whenever filterOptions input changes
    if (changes['filterOptions'] && this.filterOptions) {
      this.filteredOptions = [...this.filterOptions];

      // Reset search term when options change to show all new options
      this.searchTerm = '';
    }

    // Update valueMap when selectedValues input changes
    // ⚠️ CRITICAL FIX: Only reinitialize if values have TRULY changed
    // Prevents clearing valueMap during user interaction (between checkbox clicks)
    if (changes['selectedValues']) {
      const currentValues = changes['selectedValues'].currentValue || [];
      const previousValues = changes['selectedValues'].previousValue || [];

      // Perform deep equality check - only reinitialize if values are different
      const hasRealChange = !this.arraysAreEqual(currentValues, previousValues);


      // Only reinitialize if:
      // 1. First change (component initialization)
      // 2. OR values have genuinely changed (not just reference change)
      if (changes['selectedValues'].firstChange || hasRealChange) {
        this.initializeValueMap();
      } else {
      }
    }
  }

  /**
   * Deep equality check for arrays (supports primitive values and objects)
   * Used to detect if selectedValues has genuinely changed
   */
  private arraysAreEqual(arr1: any[], arr2: any[]): boolean {
    // Different lengths = different arrays
    if (arr1.length !== arr2.length) {
      return false;
    }

    // Both empty = equal
    if (arr1.length === 0 && arr2.length === 0) {
      return true;
    }

    // Compare serialized values (handles objects and primitives)
    const serialized1 = arr1.map(v => this.serializeValue(v)).sort();
    const serialized2 = arr2.map(v => this.serializeValue(v)).sort();

    return JSON.stringify(serialized1) === JSON.stringify(serialized2);
  }

  /**
   * Check if filter is currently active
   */
  get hasActiveFilter(): boolean {
    return this.selectedValues.length > 0 && this.selectedValues.length < this.filterOptions.length;
  }

  /**
   * Check if all options are selected
   * Uses filteredOptions to account for search filtering
   */
  get allSelected(): boolean {
    if (this.filteredOptions.length === 0) return false;

    // Check if all FILTERED options are selected
    return this.filteredOptions.every(opt => {
      const key = this.serializeValue(opt.value);
      return this.valueMap.has(key);
    });
  }

  /**
   * Check if some (but not all) options are selected
   * Uses filteredOptions to account for search filtering
   */
  get someSelected(): boolean {
    if (this.filteredOptions.length === 0) return false;

    const selectedFilteredCount = this.filteredOptions.filter(opt => {
      const key = this.serializeValue(opt.value);
      return this.valueMap.has(key);
    }).length;

    return selectedFilteredCount > 0 && selectedFilteredCount < this.filteredOptions.length;
  }

  /**
   * Handle search input change
   */
  onSearchChange(): void {
    if (!this.searchTerm || this.searchTerm.trim() === '') {
      this.filteredOptions = [...this.filterOptions];
      return;
    }

    const term = this.searchTerm.toLowerCase();
    this.filteredOptions = this.filterOptions.filter(option =>
      option.label.toLowerCase().includes(term)
    );
  }

  /**
   * Toggle select all / deselect all
   * Works with filteredOptions to respect search filtering
   */
  toggleSelectAll(checked: boolean): void {
    if (checked) {
      // Select all FILTERED options (add to existing selections)
      this.filteredOptions.forEach(opt => {
        const key = this.serializeValue(opt.value);
        this.valueMap.set(key, opt.value);
      });
    } else {
      // Deselect all FILTERED options (remove from existing selections)
      this.filteredOptions.forEach(opt => {
        const key = this.serializeValue(opt.value);
        this.valueMap.delete(key);
      });
    }
    // Trigger change detection for OnPush
    this.cdr.markForCheck();
  }

  /**
   * Check if an option is selected
   * Uses serialized key for object comparison by value
   */
  isOptionSelected(value: any): boolean {
    const key = this.serializeValue(value);
    return this.valueMap.has(key);
  }

  /**
   * Toggle individual option
   */
  toggleOption(value: any, checked: boolean): void {
    const key = this.serializeValue(value);


    if (checked) {
      this.valueMap.set(key, value);
    } else {
      this.valueMap.delete(key);
    }


    // Trigger change detection for OnPush strategy
    this.cdr.markForCheck();
  }

  /**
   * Apply filter
   */
  onApply(): void {

    // Get array of original values from value map
    const selected = Array.from(this.valueMap.values());


    // If all items are selected, it's the same as no filter
    if (selected.length === this.filterOptions.length) {
      this.filterApplied.emit([]);
    } else {
      this.filterApplied.emit(selected);
    }

    // Close menu for better UX (immediate feedback)
    this.closeMenu();
  }

  /**
   * Clear filter
   */
  onClear(): void {
    this.valueMap.clear();
    this.searchTerm = '';
    this.filteredOptions = [...this.filterOptions];
    this.filterCleared.emit();
    // Trigger change detection
    this.cdr.markForCheck();

    // Close menu for better UX (immediate feedback)
    this.closeMenu();
  }

  /**
   * Close the menu programmatically
   */
  private closeMenu(): void {
    if (this.menuTrigger) {
      this.menuTrigger.closeMenu();
    }
  }

  /**
   * TrackBy function for performance
   * Uses serialized value for stable tracking with complex objects
   */
  trackByValue = (index: number, option: { value: any; label: string; count: number }): any => {
    // Use serialized value for objects to enable stable tracking
    return this.serializeValue(option.value);
  }
}
